import { useCallback, useMemo, useRef, useState } from 'react';
import {
  MePrFoService,
  type FictionalStoryRequest,
  type ReshareInfoLike,
  type StoryRequest,
} from '@/services/meprfo';

/**
 * The rendered story for a described sequence of legs, from either story
 * endpoint, or null until it has loaded.
 */
export interface ReshareStoryTeller {
  /** The story of described legs read on from a real one. */
  story(request: StoryRequest): string | null;
  /** The story of described legs alone, told by the request's settings. */
  fictionalStory(request: FictionalStoryRequest): string | null;
}

/**
 * Server-rendered reshare stories (#447 follow-up), one per distinct request,
 * for legs that do not exist yet: the message being composed and the made-up
 * ones the preview sheet shows further along. The story's grammar lives in one
 * place, on the server, so a story is fetched, never composed.
 *
 * The teller answers from its cache and, on a miss, starts the one fetch for
 * that request and answers null until it lands; the state update then
 * re-renders whoever asked. Callers read it while composing, so the kick-off
 * happens during render — it is guarded against repeats, so a render that asks
 * twice, or React rendering twice, costs one request. A failed request is
 * logged and forgotten, so the next render asks again.
 */
export function useReshareStories(): ReshareStoryTeller {
  const [stories, setStories] = useState<Record<string, string>>({});
  const inFlight = useRef(new Set<string>());

  // One cache for both endpoints, each request keyed by which it went to.
  const tell = useCallback(
    (key: string, render: () => Promise<ReshareInfoLike>): string | null => {
      const cached = stories[key];
      if (cached !== undefined) return cached;
      if (!inFlight.current.has(key)) {
        inFlight.current.add(key);
        render()
          .then(payload => {
            setStories(prev => ({ ...prev, [key]: payload.story ?? '' }));
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error('Failed to render the reshare story:', err);
          })
          .finally(() => {
            inFlight.current.delete(key);
          });
      }
      return null;
    },
    [stories]
  );

  return useMemo(
    () => ({
      story: request =>
        tell(`story ${JSON.stringify(request)}`, () =>
          MePrFoService.renderReshareStory(request)
        ),
      fictionalStory: request =>
        tell(`fictional-story ${JSON.stringify(request)}`, () =>
          MePrFoService.renderReshareFictionalStory(request)
        ),
    }),
    [tell]
  );
}
